import { FC, useCallback, useContext, useEffect, useState } from 'react'
import { useIonRouter } from '@ionic/react'
import { App, URLOpenListenerEvent } from '@capacitor/app'
import { Plugins } from '@capacitor/core'
import branch from 'branch-sdk'
import { BranchInitEvent } from 'capacitor-branch-deep-links'

import { appConfig } from '@/services/config'
import { useShareIntentReceiver } from '@/lib/hooks/useShareIntentReceiver'
import { Context } from '@/lib/store'
import { ActionType } from '@/lib/store/reducer'
import {
  getReferralParamData,
  setLocalReferralData,
} from '@/utils/referralParams'
import { isNative } from '@/utils/utils'

import { RouterAction, RouterDirection } from '@/enums'

import { Routes } from './routes'

const { BranchDeepLinks } = Plugins

export const parseDeepUrl = (pureUrl: string) => {
  const url = decodeURIComponent(pureUrl)
  if (!url.includes('app-link') && !url.includes('republikgg-alternate')) {
    const refUsername = url.match(/ref_username=([^&]*)/)?.pop() || null
    if (!refUsername) {
      return url.split(':/').pop()
    }
    return null
  }
  return null
}

export const RoutingInterceptor: FC = ({ children }) => {
  useShareIntentReceiver()
  const { dispatch } = useContext(Context)
  const router = useIonRouter()
  const [ready, setReady] = useState(false)

  const redirectDeepLinksOnInit = useCallback(
    (url: string) => {
      router.push(url, RouterDirection.Forward, RouterAction.Push)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [router?.routeInfo?.pathname]
  )

  const handleBranchInput = useCallback(
    (desktopUrl: string, refUsername: string) => {
      dispatch({
        type: ActionType.SET_REFERRAL,
        payload: { refUsername, desktopUrl },
      })

      if (!router) {
        return
      }

      try {
        const url = new URL(desktopUrl)
        redirectDeepLinksOnInit(url.pathname)
      } catch (e) {
        console.error(`Failed to construct url: invalid url ${e}`)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch, router?.routeInfo?.pathname, redirectDeepLinksOnInit]
  )

  // This function is used only for push notifications
  useEffect(() => {
    const appUrlOpenHandler = App.addListener(
      'appUrlOpen',
      (event: URLOpenListenerEvent) => {
        const url = parseDeepUrl(event.url)
        if (url.includes(Routes.SSO_FACEBOOK_AUTHORIZE)) {
          return
        }

        if (url) {
          redirectDeepLinksOnInit(url)
        }
      }
    )

    return () => {
      appUrlOpenHandler.remove()
    }
  }, [redirectDeepLinksOnInit])

  useEffect(() => {
    // Only use native ios and andorid excluding ios and andorid web
    if (isNative()) {
      if (BranchDeepLinks) {
        BranchDeepLinks.addListener('init', async (event: BranchInitEvent) => {
          setLocalReferralData(
            event.referringParams?.$desktop_url,
            event.referringParams?.refUsername
          )
          const { desktopUrl, refUsername } = await getReferralParamData(
            event.referringParams?.$desktop_url,
            event.referringParams?.refUsername
          )

          if (
            event.referringParams?.$desktop_url &&
            event.referringParams?.refUsername
          ) {
            handleBranchInput(desktopUrl, refUsername)
          }
        })

        BranchDeepLinks.addListener('initError', (error) => {
          console.error('Branchio error: ', JSON.stringify(error))
        })
        setReady(true)
      }
    } else {
      branch.init(appConfig.branchKey, undefined, async (error, data) => {
        setLocalReferralData(
          data?.data_parsed?.$desktop_url,
          data?.data_parsed?.refUsername
        )
        const { desktopUrl, refUsername } = await getReferralParamData(
          data?.data_parsed?.$desktop_url,
          data?.data_parsed?.refUsername
        )

        if (error) {
          console.error('Branchio error: ', JSON.stringify(error))
        } else if (
          data?.data_parsed?.$desktop_url &&
          data?.data_parsed?.refUsername
        ) {
          handleBranchInput(desktopUrl, refUsername)
        }
        setReady(true)
      })
    }

    return () => {
      console.debug('Delete Branch')
    }
  }, [handleBranchInput])

  return <>{ready ? children : null}</>
}
